import {
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import { common } from '@mui/material/colors';
import { ReactComponent as AddressIcon } from 'assets/images/icons/payment/address.svg';
import { ReactComponent as UserProfileIcon } from 'assets/images/icons/payment/bill-payee.svg';
import { ReactComponent as CompanyNameIcon } from 'assets/images/icons/payment/company-name.svg';
import { ReactComponent as EmailIcon } from 'assets/images/icons/payment/email.svg';
import { ReactComponent as ProductIcon } from 'assets/images/icons/payment/payment-product.svg';
import { ReactComponent as PaymentTimeIcon } from 'assets/images/icons/payment/payment-time.svg';
import { ReactComponent as TotalAmountIcon } from 'assets/images/icons/payment/payment-totla-amount.svg';
import { ReactComponent as PaymentTypeIcon } from 'assets/images/icons/payment/payment-type.svg';
import { ReactComponent as PhoneIcon } from 'assets/images/icons/payment/phone.svg';
import { ReactComponent as TaxCodeIcon } from 'assets/images/icons/payment/tax-code.svg';
import React, { useEffect } from 'react';
import { createStyleSheet, handleError } from 'utils/helpers';
import PaymentExportButton from './components/payment-export';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { paymentDetailById, updatePayment } from 'services/payment.service';
import {
    convertValuePaymentStatus,
    convertValuePaymentType,
} from 'pages/admin/payment/constants';
import { toast } from 'react-toastify';
import numeral from 'plugins/numeral';
import { convertValueAccountType } from 'pages/admin/account-management/constants';
import PaymentConstants from 'pages/admin/payment/constants';
import { savePDF } from '@progress/kendo-react-pdf';
import ModalConfirm from 'common/components/modal-confirm';
import { useAppSelector } from 'common/hook';
import { RootState } from 'plugins/store';

const PaymentDetail: React.FC = () => {
    const [searchParams] = useSearchParams();
    const transactionStatus = searchParams.get('vnp_TransactionStatus');

    const userInfo = useAppSelector((state: RootState) => state.user);

    useEffect(() => {
        if (transactionStatus === '00') {
            toast.success('Thanh toán thành công');
        } else if (transactionStatus) {
            toast.error('Thanh toán không thành công!');
        }
    }, [transactionStatus]);
    const location = useLocation();
    const [openModalConfirm, setOpenModalConfirm] = React.useState(false);

    const container = React.useRef(null);
    const paymentId = useParams().id;
    const [dataRows, setDataRows] = React.useState<
        {
            paymentType: string;
            paymentTypeNote: string;
            detail: string;
            detailNote: string;
            quantity: string;
            unitPrice: number | null;
            discountPercent: number | null;
            totalPrice: number | null;
        }[]
    >([]);
    const [paymentDetail, setPaymentDetail] = React.useState<{
        id: number;
        status: string;
        createdAt: string;
        paymentType: string;
        productName: string;
        productNote: string;
        totalAmount: number;
        invoiceCompanyName: string;
        invoiceTaxCode: string;
        invoiceAddress: string;
        invoiceReceiverName: string;
        invoiceReceiverEmail: string;
        invoiceReceiverPhone: string;
        createdByName: string;
        createdByRole: string;
        createdByEmail: string;
        overlapProductDiscount: number;
        preTaxPrice: number;
        tax: number;
        paymentUrl: string;
        paymentMethod?: string;
    }>({
        id: 0,
        status: '',
        createdAt: '',
        paymentType: '',
        productName: '',
        productNote: '',
        totalAmount: 0,
        invoiceCompanyName: '',
        invoiceTaxCode: '',
        invoiceAddress: '',
        invoiceReceiverName: '',
        invoiceReceiverEmail: '',
        invoiceReceiverPhone: '',
        createdByName: '',
        createdByRole: '',
        createdByEmail: '',
        overlapProductDiscount: 0,
        preTaxPrice: 0,
        tax: 0,
        paymentUrl: '',
        paymentMethod: '',
    });

    const exportPDFWithMethod = () => {
        const element = container.current || document.body;
        savePDF(element, {
            paperSize: 'auto',
            margin: 40,
            fileName: `Chi tiết thanh toán`,
        });
    };

    const paymentStatusStyle = React.useMemo(() => {
        return {
            background:
                paymentDetail.status === PaymentConstants.status.PAID
                    ? '#DDFFEB'
                    : '#FFEBE8',
            color:
                paymentDetail.status === PaymentConstants.status.PAID
                    ? '#13B75B'
                    : '#D92D2A',
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
        };
    }, [paymentDetail.status]);

    const addressMap: Record<number, string> = {
        60: 'Hồ Chí Minh',
        17: 'Đà Nẵng',
        26: 'Hà Nội',
    };

    const genderMap: Record<number, string> = {
        1: 'Nam',
        2: 'Nữ',
    };

    const marriageMap: Record<number, string> = {
        66: 'Độc thân',
        67: 'Đã kết hôn',
    };

    const childMap: Record<number, string> = {
        68: 'Chưa có con',
        69: 'Đã có con',
    };

    const careerFieldMap: Record<number, string> = {
        81: 'Vận tải - Logistic',
        82: 'Bảo hiểm',
        83: 'Bán lẻ',
        84: 'Du lịch - Nhà hàng - Khách sạn',
        85: 'Bất động sản - Xây dựng',
        86: 'F&B',
        87: 'Ô tô - Xe máy',
        88: 'Viễn thông',
        89: 'Tài chính - Ngân hàng',
        90: 'Công nghệ',
        91: 'FMCG',
        92: 'Chăm sóc sức khoẻ',
        93: 'Y tế',
    };

    const careerMap: Record<number, string> = {
        70: 'Công chức nhà nước',
        71: 'Nhân viên văn phòng công ty tư nhân',
        72: 'Chủ doanh nghiệp/Doanh nhân',
        73: 'Tự kinh doanh riêng',
        74: 'Lao động trình độ cao (Bác sĩ, kỹ sư, luật sư, ...)',
        75: 'Freelancer',
        76: 'Sinh viên',
        77: 'Công nhân',
        78: 'Nông dân',
        79: 'Nội trợ / hưu trí',
    };

    const incomeMap: Record<string, string> = {
        '0-9999999': 'Dưới 10 triệu VND/tháng',
        '10000000-29999999': 'Từ 10 - dưới 30 triệu VND/tháng',
        '30000000-59999999': 'Từ 30 - dưới 60 triệu VND/tháng',
        '60000000-0': 'Trên 60 triệu VND/tháng',
    };

    const handleConfirmPaymentBankTransfer = async () => {
        setOpenModalConfirm(false);
        // Logic to confirm payment via bank transfer
        // This could involve calling an API to update the payment status
        toast.success('Xác nhận thanh toán qua chuyển khoản thành công!');
        const response = await updatePayment(Number(paymentId), {
            status: PaymentConstants.status.PAID,
        });
        if (response.success) {
            // refetch payment details or update state
            toast.success('Cập nhật trạng thái thanh toán thành công!');
            fetchPaymentDetails(paymentId || '');
        } else {
            toast.error('Cập nhật trạng thái thanh toán thất bại!');
        }
    };

    const formatPaymentDetails = (
        detail: string,
        detailNote: string | number
    ): string => {
        const noteStr = String(detailNote);
        const noteNum = Number(detailNote);

        switch (detail) {
            case 'address':
                return addressMap[noteNum] ?? '';
            case 'gender':
                return genderMap[noteNum] ?? '';
            case 'dob':
                return `${noteStr} (tuổi)`;
            case 'marriage':
                return marriageMap[noteNum] ?? '';
            case 'child':
                return childMap[noteNum] ?? '';
            case 'career_field':
                return careerFieldMap[noteNum] ?? '';
            case 'career':
                return careerMap[noteNum] ?? '';
            case 'monthly_personal_income':
            case 'monthly_family_income':
                return incomeMap[noteStr] ?? '';
            default:
                return '';
        }
    };

    const renderedPaymentTypeNote = new Set();

    const checkRenderedPaymentTypeNote = (paymentTypeNote: string): string => {
        if (renderedPaymentTypeNote.has(paymentTypeNote)) {
            return '';
        }
        renderedPaymentTypeNote.add(paymentTypeNote);
        return paymentTypeNote;
    };

    const paymentInfoLabels = [
        { xs: 2, label: 'Dịch vụ' },
        { xs: 3, label: 'Chi tiết dịch vụ' },
        { xs: 1, label: 'Số lượng' },
        { xs: 2, label: 'Đơn giá' },
        { xs: 2, label: 'Tỷ lệ chiết khấu' },
        { xs: 2, label: 'Thành tiền' },
    ];

    const mapPaymentMethod = (method: string | undefined): string => {
        switch (method) {
            case 'vnpay':
                return 'VNPay';
            case 'momo':
                return 'Momo';
            case 'bank_transfer':
                return 'Chuyển khoản';
            case 'cash':
                return 'Tiền mặt';
            default:
                return 'Không xác định';
        }
    };

    const fetchPaymentDetails = async (id: string) => {
        try {
            const response = await paymentDetailById(Number(id));
            if (response.success) {
                const attributes = response.data.payment.attributes;
                setPaymentDetail({
                    id: attributes.id,
                    status: attributes.status,
                    createdAt: attributes.created_at_for_list,
                    paymentType: attributes.product_type,
                    productName: attributes.product?.name,
                    productNote: attributes.product?.note,
                    totalAmount: attributes.total_price,
                    invoiceCompanyName: attributes.client_company_name || '',
                    invoiceTaxCode: attributes.client_tax_number || '',
                    invoiceAddress: attributes.client_company_address || '',
                    invoiceReceiverName: attributes.client_name || '',
                    invoiceReceiverEmail: attributes.client_email || '',
                    invoiceReceiverPhone: attributes.client_phone || '',
                    createdByName: attributes.created_by.name || '',
                    createdByRole: attributes.created_by.role || '',
                    createdByEmail: attributes.created_by.email || '',
                    overlapProductDiscount:
                        attributes.product_overlap_discount || 0,
                    preTaxPrice: attributes.total_price - attributes.tax,
                    tax: attributes.tax || 0,
                    paymentUrl: attributes.url_payment || '',
                    paymentMethod: attributes.payment_method || '',
                });
                if (attributes.product_type === PaymentConstants.type.PLAN) {
                    setDataRows([
                        {
                            paymentType: convertValuePaymentType(
                                attributes.product_type
                            ),
                            paymentTypeNote: '',
                            detail: attributes.product?.name,
                            detailNote: '',
                            quantity: '1',
                            unitPrice: attributes.price_unit,
                            discountPercent:
                                attributes.product?.discount_percent || 0,
                            totalPrice:
                                attributes.price_unit - attributes.discount,
                        },
                    ]);
                } else {
                    const paymentDetails = [];
                    // push the first item
                    const firstItem = attributes.payment_details[0];
                    paymentDetails.push({
                        paymentType: '- Giá base',
                        paymentTypeNote:
                            '(Chi phí / phản hồi ngẫu nhiên / câu hỏi)',
                        detail: '',
                        detailNote: '',
                        quantity: `${attributes.total_answer}`,
                        unitPrice: firstItem.attributes.price_unit,
                        discountPercent: firstItem.attributes.discount_percent,
                        totalPrice:
                            attributes.total_answer *
                            firstItem.attributes.price_unit *
                            (1 - firstItem.attributes.discount_percent / 100),
                    });
                    // push an empty item
                    paymentDetails.push({
                        paymentType: '- Giá targeting',
                        paymentTypeNote:
                            '(Chi phí nhắm đối tượng mục tiêu / câu hỏi)',
                        detail: '',
                        detailNote: '',
                        quantity: '',
                        unitPrice: null,
                        discountPercent: null,
                        totalPrice: null,
                    });
                    // push the rest items
                    for (
                        let i = 1;
                        i < attributes.payment_details.length;
                        i++
                    ) {
                        const item = attributes.payment_details[i];
                        paymentDetails.push({
                            paymentType: '',
                            paymentTypeNote:
                                PaymentConstants.fieldName[
                                    item.attributes
                                        .field_name as keyof typeof PaymentConstants.fieldName
                                ],
                            detail: item.attributes.field_name,
                            detailNote: item.attributes.field_value,
                            quantity: `${attributes.total_answer}`,
                            unitPrice: item.attributes.price_unit,
                            discountPercent: item.attributes.discount_percent,
                            totalPrice:
                                attributes.total_answer *
                                item.attributes.price_unit *
                                (1 - item.attributes.discount_percent / 100),
                        });
                    }
                    setDataRows(paymentDetails);
                }
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            handleError(error);
        }
    };

    React.useEffect(() => {
        if (paymentId) {
            fetchPaymentDetails(paymentId);
        }
    }, [paymentId]);

    return (
        <Box mt={5}>
            <Box display="flex" justifyContent="space-between" alignItems="end">
                <Typography
                    variant="h3"
                    component="h3"
                    sx={{
                        fontWeight: '700',
                        fontSize: 28,
                        lineHeight: '33.89px',
                        color: (theme) => theme.palette.primary.main,
                    }}
                >
                    Chi tiết thanh toán
                </Typography>
                <PaymentExportButton onClick={exportPDFWithMethod} />
            </Box>
            <Box mt={5} ref={container}>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={3}>
                        <Box
                            sx={{
                                borderRadius: '15px',
                                height: '100%',
                                overflow: 'auto',
                                backgroundColor: common.white,
                                padding: 3,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: '14px',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '18px',
                                        lineHeight: '28px',
                                        fontWeight: 500,
                                        color: '#1A1C21',
                                    }}
                                >
                                    ID {paymentDetail.id}
                                </Typography>
                                {paymentDetail.status && (
                                    <Chip
                                        label={convertValuePaymentStatus(
                                            paymentDetail.status
                                        )}
                                        sx={paymentStatusStyle}
                                    />
                                )}
                            </Box>
                            <Box sx={styles.paymentInfoBox}>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <PaymentTimeIcon /> Thời gian:
                                    </Stack>
                                </Typography>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    {paymentDetail.createdAt}
                                </Typography>
                            </Box>
                            <Box sx={styles.paymentInfoBox}>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <PaymentTypeIcon /> Loại:
                                    </Stack>
                                </Typography>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    {convertValuePaymentType(
                                        paymentDetail.paymentType
                                    )}
                                </Typography>
                            </Box>
                            <Box sx={styles.paymentInfoBox}>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <ProductIcon /> Sản phẩm:
                                    </Stack>
                                </Typography>
                                <Typography
                                    sx={{
                                        ...styles.paymentInfoBox.info,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end',
                                    }}
                                >
                                    {paymentDetail.productName} <br />
                                    <Typography
                                        component={'span'}
                                        sx={{
                                            color: '#667085',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            lineHeight: '18px',
                                        }}
                                    >
                                        {paymentDetail.productNote}
                                    </Typography>
                                </Typography>
                            </Box>
                            <Box sx={styles.paymentInfoBox}>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <TotalAmountIcon /> Phương thức:
                                    </Stack>
                                </Typography>
                                <Typography>
                                    {mapPaymentMethod(
                                        paymentDetail.paymentMethod
                                    )}
                                </Typography>
                            </Box>
                            <Box sx={styles.paymentInfoBox}>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <TotalAmountIcon /> Tổng chi phí:
                                    </Stack>
                                </Typography>
                                <Typography sx={styles.totalAmount}>
                                    {numeral(
                                        paymentDetail.totalAmount
                                    ).format()}{' '}
                                    VND
                                </Typography>
                            </Box>
                            <Divider
                                sx={{
                                    borderTop: '1px dashed #000',
                                    mb: '14px',
                                }}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: '14px',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '18px',
                                        lineHeight: '28px',
                                        fontWeight: 500,
                                        color: '#1A1C21',
                                    }}
                                >
                                    Hóa đơn
                                </Typography>
                            </Box>
                            <Box sx={styles.paymentInfoBox}>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <CompanyNameIcon /> Tên doanh nghiệp:
                                    </Stack>
                                </Typography>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    {paymentDetail.invoiceCompanyName}
                                </Typography>
                            </Box>
                            <Box sx={styles.paymentInfoBox}>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <TaxCodeIcon /> Mã số thuế:
                                    </Stack>
                                </Typography>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    {paymentDetail.invoiceTaxCode}
                                </Typography>
                            </Box>
                            <Box sx={styles.paymentInfoBox}>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <AddressIcon /> Địa chỉ doanh nghiệp:
                                    </Stack>
                                </Typography>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    {paymentDetail.invoiceAddress}
                                </Typography>
                            </Box>
                            <Box sx={styles.paymentInfoBox}>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <UserProfileIcon /> Tên người nhận hóa
                                        đơn:
                                    </Stack>
                                </Typography>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    {paymentDetail.invoiceReceiverName}
                                </Typography>
                            </Box>
                            <Box sx={styles.paymentInfoBox}>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <EmailIcon /> Email:
                                    </Stack>
                                </Typography>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    {paymentDetail.invoiceReceiverEmail}
                                </Typography>
                            </Box>
                            <Box sx={styles.paymentInfoBox}>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <PhoneIcon /> Phone:
                                    </Stack>
                                </Typography>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    {paymentDetail.invoiceReceiverPhone}
                                </Typography>
                            </Box>
                            <Divider
                                sx={{
                                    borderTop: '1px dashed #000',
                                    mb: '14px',
                                }}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: '14px',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '18px',
                                        lineHeight: '28px',
                                        fontWeight: 500,
                                        color: '#1A1C21',
                                    }}
                                >
                                    Người tạo
                                </Typography>
                            </Box>

                            <Box sx={styles.paymentInfoBox}>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <UserProfileIcon /> Người tạo:
                                    </Stack>
                                </Typography>
                                <Typography
                                    sx={{
                                        ...styles.paymentInfoBox.info,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end',
                                        fontWeight: 700,
                                    }}
                                >
                                    {paymentDetail.invoiceReceiverName} <br />
                                    <Typography sx={styles.paymentInfoBox.info}>
                                        {convertValueAccountType(
                                            paymentDetail.createdByRole
                                        )}
                                    </Typography>
                                </Typography>
                            </Box>
                            <Box sx={styles.paymentInfoBox}>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <EmailIcon /> Email:
                                    </Stack>
                                </Typography>
                                <Typography sx={styles.paymentInfoBox.info}>
                                    {paymentDetail.createdByEmail}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={9}>
                        <Box
                            sx={{
                                borderRadius: '15px',
                                height: '100%',
                                overflow: 'auto',
                                backgroundColor: common.white,
                                padding: 3,
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 500,
                                    lineHeight: '28px',
                                    mb: '18px',
                                }}
                            >
                                Chi tiết
                            </Typography>
                            <Box
                                sx={{
                                    width: '100%',
                                    border: '1px solid #F0F1F3',
                                }}
                            >
                                <Grid
                                    container
                                    sx={{
                                        p: 2,
                                        mb: 1,
                                        backgroundColor:
                                            'var(--color-primary-lighter)',
                                    }}
                                >
                                    {paymentInfoLabels.map((item, index) => (
                                        <Grid key={index} item xs={item.xs}>
                                            <Typography
                                                sx={styles.paymentInfoBox.info}
                                            >
                                                {item.label}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>

                                {/* Rows */}
                                {dataRows.map((row, index) => (
                                    <Grid
                                        container
                                        key={index}
                                        p={2}
                                        sx={{
                                            py: 1,
                                        }}
                                    >
                                        <Grid item xs={2}>
                                            <Typography
                                                sx={styles.paymentInfoBox.info}
                                            >
                                                {row.paymentType}
                                            </Typography>
                                            <Typography
                                                sx={
                                                    row.paymentType
                                                        ? {
                                                              fontSize: '12px',
                                                              fontWeight: 400,
                                                              lineHeight:
                                                                  '18px',
                                                              color: '#667085',
                                                          }
                                                        : styles.paymentInfoBox
                                                              .info
                                                }
                                            >
                                                {checkRenderedPaymentTypeNote(
                                                    row.paymentTypeNote
                                                )}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Grid item xs={3}>
                                                {formatPaymentDetails(
                                                    row.detail,
                                                    row.detailNote
                                                ) ? (
                                                    <Typography
                                                        sx={
                                                            styles.paymentDetails
                                                        }
                                                    >
                                                        {formatPaymentDetails(
                                                            row.detail,
                                                            row.detailNote
                                                        )}
                                                    </Typography>
                                                ) : (
                                                    <>
                                                        <Typography
                                                            sx={
                                                                styles.paymentDetails
                                                            }
                                                        >
                                                            {row.detail}
                                                        </Typography>
                                                        <Typography
                                                            sx={
                                                                styles.paymentDetails
                                                            }
                                                        >
                                                            {row.detailNote}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <Typography
                                                sx={styles.paymentInfoBox.info}
                                            >
                                                {row.quantity}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2}>
                                            {row.unitPrice && (
                                                <Typography
                                                    sx={
                                                        styles.paymentInfoBox
                                                            .info
                                                    }
                                                >
                                                    {numeral(
                                                        row.unitPrice
                                                    ).format()}{' '}
                                                    VND
                                                </Typography>
                                            )}
                                        </Grid>
                                        <Grid item xs={2}>
                                            {row.discountPercent && (
                                                <Typography
                                                    sx={
                                                        styles.paymentInfoBox
                                                            .info
                                                    }
                                                >
                                                    {row.discountPercent} %
                                                </Typography>
                                            )}
                                        </Grid>
                                        <Grid item xs={2}>
                                            {row.totalPrice && (
                                                <Typography
                                                    sx={
                                                        styles.paymentInfoBox
                                                            .info
                                                    }
                                                >
                                                    {numeral(
                                                        row.totalPrice
                                                    ).format()}{' '}
                                                    VND
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                ))}

                                <Grid
                                    container
                                    spacing={4}
                                    sx={{
                                        p: 2,
                                        mt: 2,
                                    }}
                                >
                                    {paymentDetail.paymentType ===
                                        PaymentConstants.type.PLAN && (
                                        <>
                                            <Grid item xs={8} textAlign="right">
                                                <Typography
                                                    sx={
                                                        styles.paymentInfoBox
                                                            .info
                                                    }
                                                >
                                                    Giảm trừ thanh toán trước:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4} textAlign="right">
                                                <Typography
                                                    sx={
                                                        styles.paymentInfoBox
                                                            .info
                                                    }
                                                >
                                                    -{' '}
                                                    {numeral(
                                                        paymentDetail.overlapProductDiscount
                                                    ).format()}{' '}
                                                    VND
                                                </Typography>
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={8} textAlign="right">
                                        <Typography
                                            sx={styles.paymentInfoBox.info}
                                        >
                                            Chi phí trước thuế:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4} textAlign="right">
                                        <Typography
                                            sx={styles.paymentInfoBox.info}
                                        >
                                            {numeral(
                                                paymentDetail.preTaxPrice
                                            ).format()}{' '}
                                            VND
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={8} textAlign="right">
                                        <Typography
                                            sx={styles.paymentInfoBox.info}
                                        >
                                            VAT (10%):
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4} textAlign="right">
                                        <Typography
                                            sx={styles.paymentInfoBox.info}
                                        >
                                            +{' '}
                                            {numeral(
                                                paymentDetail.tax
                                            ).format()}{' '}
                                            VND
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={8} textAlign="right">
                                        <Typography
                                            sx={styles.totalAmountLabel}
                                        >
                                            Tổng chi phí :
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4} textAlign="right">
                                        <Typography sx={styles.totalAmount}>
                                            {numeral(
                                                paymentDetail.totalAmount
                                            ).format()}{' '}
                                            VND
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            {paymentDetail.status ===
                                PaymentConstants.status.PENDING && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    {paymentDetail.paymentMethod ===
                                        'bank_transfer' &&
                                        (userInfo.attributes.role === 'super' ||
                                            userInfo.attributes.role ===
                                                'accountant') && (
                                            <Button
                                                variant="contained"
                                                sx={{
                                                    fontSize: '20px',
                                                    textTransform: 'none',
                                                    lineHeight: '24.2px',
                                                    fontWeight: 700,
                                                    minHeight: '60px',
                                                    minWidth: '177px',
                                                    borderRadius: '39.06px',
                                                }}
                                                onClick={() => {
                                                    setOpenModalConfirm(true);
                                                }}
                                            >
                                                Xác nhận đã thanh toán
                                            </Button>
                                        )}
                                    {paymentDetail.paymentUrl && (
                                        <Button
                                            variant="contained"
                                            sx={{
                                                fontSize: '20px',
                                                textTransform: 'none',
                                                lineHeight: '24.2px',
                                                fontWeight: 700,
                                                minHeight: '60px',
                                                minWidth: '177px',
                                                borderRadius: '39.06px',
                                                marginLeft: '16px',
                                            }}
                                            onClick={() => {
                                                localStorage.setItem(
                                                    'urlCallback',
                                                    location.pathname
                                                );
                                                window.open(
                                                    paymentDetail.paymentUrl,
                                                    '_blank'
                                                );
                                            }}
                                        >
                                            Thanh toán
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <ModalConfirm
                open={openModalConfirm}
                onClose={() => setOpenModalConfirm(false)}
                title={'Xác nhận'}
                textOk="Xác nhận"
                onOk={handleConfirmPaymentBankTransfer}
                content={
                    <Box>
                        Xác nhận khách hàng đã thanh toán qua chuyển khoản ngân
                        hàng?
                    </Box>
                }
            />
        </Box>
    );
};

const styles = createStyleSheet({
    paymentInfoBox: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: '14px',
        info: {
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            color: '#1A1C21',
        },
    },
    totalAmount: {
        fontSize: 16,
        lineHeight: '24px',
        letterSpacing: '0.5%',
        color: '#0D894F',
        fontWeight: '700',
    },
    totalAmountLabel: {
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0.5%',
        fontWeight: '500',
    },
    paymentDetails: {
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
        width: '400%',
        paddingTop: '3px',
    },
});

export default PaymentDetail;
